import { formatDateToISO, getBusinessTodayDate } from './dateUtils';

/**
 * Normalize gender values to single-letter codes (M/F/U).
 *
 * Accepts various formats from database:
 * - M, F, U (single letter - already normalized)
 * - Male, Female, Unknown (full word from legacy data)
 * - Case-insensitive variants
 *
 * @returns Normalized single-letter code (M/F/U) or null if value is empty/invalid.
 */
export const normalizeGender = (
  value: string | null | undefined
): 'M' | 'F' | 'U' | null => {
  if (!value || typeof value !== 'string') return null;

  const genderStr = value.trim().toLowerCase();

  if (genderStr === 'm' || genderStr === 'male') {
    return 'M';
  } else if (genderStr === 'f' || genderStr === 'female') {
    return 'F';
  } else if (genderStr === 'u' || genderStr === 'unknown') {
    return 'U';
  }

  return null;
};

/**
 * Get display label for gender code.
 *
 * @param genderCode - Single letter code (M/F/U) or full word
 * @returns Human-readable label (Male/Female/Unknown)
 */
export const getGenderLabel = (
  genderCode: string | null | undefined
): string => {
  const normalized = normalizeGender(genderCode);
  switch (normalized) {
    case 'M':
      return 'Male';
    case 'F':
      return 'Female';
    case 'U':
      return 'Unknown';
    default:
      return genderCode || '';
  }
};

// Format SSN as user types: 123-45-6789
export const formatSSN = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

// Format phone as user types: 555-123-4567
export const formatPhone = (value: string): string => {
  let digits = value.replace(/\D/g, '');
  // Remove +1 country code if present (11 digits starting with 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Format a stored phone for edit forms; blank when not exactly 10 US digits. */
export const formatStoredPhone = (value: string | null | undefined): string => {
  if (!value) return '';
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) return '';
  return formatPhone(digits);
};

// Format zip code as user types: 12345 or 12345-6789
export const formatZipCode = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

/** Sanitize MBI as the user types (no dashes; uppercase alphanumeric only). */
export const formatMbi = (value: string): string => {
  return value.trim().replace(/[\s-]/g, '').toUpperCase();
};

// Language mapping for Kinnser integration
// 1: English, 2: Spanish, 3: Chinese, 4: Russian, 5: Vietnamese, 6: Other/Unknown
export const mapLanguageToKey = (
  language?: string | null
): {
  language_key: '1' | '2' | '3' | '4' | '5' | '6';
  other_language?: string;
} => {
  if (!language) {
    return { language_key: '6' }; // Other/Unknown
  }

  const lang = language.toLowerCase().trim();

  switch (lang) {
    case 'english':
    case 'en':
      return { language_key: '1' };
    case 'spanish':
    case 'es':
      return { language_key: '2' };
    case 'chinese':
    case 'zh':
      return { language_key: '3' };
    case 'russian':
    case 'ru':
      return { language_key: '4' };
    case 'vietnamese':
    case 'vi':
      return { language_key: '5' };
    default:
      return { language_key: '6', other_language: language };
  }
};

/**
 * Default date range for the Intake Manager filter, as YYYY-MM-DD strings.
 *
 * Mirrors the backend intake list and export defaults: the end date is today
 * (Pacific business date) and the start date is six weeks earlier. Returning
 * concrete strings lets the UI show the exact window the API applies instead of
 * leaving the pickers blank while the backend silently applies the same range.
 */
export const getDefaultIntakeDateRange = (): {
  startDate: string;
  endDate: string;
} => {
  const today = getBusinessTodayDate();
  const start = new Date(today);
  start.setDate(start.getDate() - 6 * 7);
  return {
    startDate: formatDateToISO(start),
    endDate: formatDateToISO(today),
  };
};
